/**
 * The returning-member "Welcome back" prompt, mounted at the app root.
 *
 * After login it checks every program the user belongs to and prompts them
 * to fill any program-level mandatory optional fields they haven't completed
 * yet (union across all their programs / roles). The same checklist carries
 * the member-attributes confirmation (useMemberAttributesPrompt), so a member
 * sees one "Welcome back" card instead of two stacked modals. When attributes
 * are the only thing outstanding, the attributes editor opens directly.
 *
 * Dismissal: the checklist blocks the app until everything is complete. If a
 * save attempt fails (or the user opens an auxiliary editor and the field
 * stays unfilled), internal Dosen team users can "Complete later": a
 * sessionStorage flag keeps the checklist closed until the tab is closed.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchUserProfile, isProfileFresh } from '../../../redux/slices/user-profile/userProfieSlice';
import { popupActions } from '../../../redux/slices/onboardingPopupsSlice';
import { getUserDetails } from '../../../utils/orgName';
import ROLES from '../../../utils/roles';
import { DOSEN_TEAM_ORG_NAME, MANDATORY_FIELDS_DISABLED_ORG_IDS } from '../../../utils/constants';
import MissingMandatoryFieldsModal from './MissingMandatoryFieldsModal';
import MemberAttributesModal from './MemberAttributesModal';
import EditProfile from './EditProfile';
import WorkHistoryModal from './WorkHistoryModal';
import EducationModal from './EducationModal';
import SocialHandlesModal from './SocialHandlesModal';
import LearningObjectivesNewModal from '../tabs/profile/LearningObjectivesNewModal';
import useMemberAttributesPrompt from './useMemberAttributesPrompt';
import { onboardingTheme } from '../onboarding/onboardingTheme';
import {
  FieldMeta,
  UserGroupInfo,
  getMissingMandatoryFields
} from '../utils/missingMandatoryFields';

interface GroupRoleItem {
  id: string;
  role: string;
  active?: boolean;
  // Mandatory-fields config now ships inline on the groupsListForUser payload,
  // so we no longer fetch each group document separately to read it.
  userOptionalFields?: UserGroupInfo['userOptionalFields'];
}

const DISMISSED_KEY = 'dosen_missing_mandatory_dismissed';

export default function MissingMandatoryFieldsTrigger() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const userState = useAppSelector((state) => state.userProfile);
  const groupRoleList = useAppSelector((state) => state.groupRoleList);
  const selectedGroupRole = useAppSelector((state) => state.selectedGroup.data?.role);

  const role = localStorage.getItem('role') || '';
  const profileData = userState.data;
  // Same as useMemberAttributesPrompt: a profile fetched since this load, kept
  // through refetches so the checklist does not close mid-answer.
  const isLoaded = isProfileFresh(userState);
  const isFtue = profileData?.personal_details?.ftue === true;
  // When the user is viewing the admin dashboard (program admin context), skip
  // the mandatory-fields prompt — those fields are for member-facing experience.
  const isViewingAsAdmin = selectedGroupRole === 'admin';

  // When FTUE transitions from true → false (user just finished onboarding),
  // some onboarding forms dispatch local updates that don't fully reflect what
  // the BE persisted (e.g. BioFeildForm reads `user_metadata.bio` which the
  // BE doesn't return). Refetch so isFilled checks use the BE's source of truth.
  const prevIsFtueRef = useRef(isFtue);
  useEffect(() => {
    if (prevIsFtueRef.current && !isFtue) {
      dispatch(fetchUserProfile());
    }
    prevIsFtueRef.current = isFtue;
  }, [isFtue, dispatch]);
  // Internal Dosen team members shouldn't be prompted to fill program-mandatory
  // fields — they're often testing across orgs.
  const orgName = (getUserDetails().orgName || '').trim().toLowerCase();
  const isDosenTeam = orgName === DOSEN_TEAM_ORG_NAME.toLowerCase();
  // Per-org opt-out: never surface the modal for orgs listed in
  // MANDATORY_FIELDS_DISABLED_ORG_IDS (matched against the Auth0 org id).
  const currentOrgId = getUserDetails().orgId || '';
  const isOrgOptedOut = MANDATORY_FIELDS_DISABLED_ORG_IDS.includes(currentOrgId);

  const groupItems: GroupRoleItem[] = useMemo(() => {
    const all = (groupRoleList.data ?? []) as GroupRoleItem[];
    // Admins don't have profile-completeness requirements — only check mentee/mentor entries.
    // Also dedupe by program id + role and drop inactive programs.
    const seen = new Set<string>();
    return all.filter((g) => {
      if (!g?.id || !g?.role) return false;
      if (g.active === false) return false;
      if (g.role === 'admin') return false;
      const key = `${g.id}::${g.role}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [groupRoleList.data]);

  const [openLearningObjectives, setOpenLearningObjectives] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === 'true'
  );

  // Member attributes join the same checklist. Once confirmed here, the row
  // stays (as Completed) until the checklist goes away, even though the
  // requirement itself has already cleared.
  const attributesPrompt = useMemberAttributesPrompt();
  const [attributesEditorOpen, setAttributesEditorOpen] = useState(false);
  const [attributesDone, setAttributesDone] = useState(false);
  const attributesRequired = attributesPrompt.required;
  const showAttributesRow = attributesRequired || attributesDone;

  const shouldCheck =
    isLoaded && !isFtue && role !== ROLES.platAdmin && !isViewingAsAdmin && !isOrgOptedOut && groupItems.length > 0;

  // userOptionalFields now ships inline on each groupsListForUser item (one
  // batched query server-side), so the mandatories are derived synchronously
  // instead of fetching every group document one-by-one (was N round-trips —
  // one per program the user belonged to).
  const enrichedGroups: UserGroupInfo[] = useMemo(() => {
    if (!shouldCheck) return [];
    return groupItems.map((g) => ({
      grpId: g.id,
      role: g.role,
      userOptionalFields: g.userOptionalFields ?? {}
    }));
  }, [shouldCheck, groupItems]);

  const missingFields: FieldMeta[] = useMemo(() => {
    if (!shouldCheck) return [];
    const missing = getMissingMandatoryFields(enrichedGroups, profileData);
    // eslint-disable-next-line no-console
    console.log('[MissingMandatoryFieldsTrigger]', {
      enrichedGroups,
      'profileData.bio': profileData?.bio,
      'profileData.bio.workHistory': (profileData?.bio as any)?.workHistory,
      'profileData.workHistory': (profileData as any)?.workHistory,
      missingFieldNames: missing.map((f) => f.name)
    });
    return missing;
  }, [shouldCheck, enrichedGroups, profileData]);

  const hasMissing = missingFields.length > 0;

  const checklistFields: FieldMeta[] = useMemo(() => {
    if (!showAttributesRow) return missingFields;
    const attributesRow: FieldMeta = {
      name: 'memberAttributes',
      label: 'Attributes',
      kind: 'externalModal',
      externalAction: 'memberAttributes',
      isFilled: () => attributesDone,
      helper: 'Confirm the attributes your organization uses to match you'
    };
    return [...missingFields, attributesRow];
  }, [missingFields, showAttributesRow, attributesDone]);

  const handleSaved = () => {
    // Refetch user profile so isFilled checks pick up the freshly saved data
    dispatch(fetchUserProfile());
  };

  const handleOpenExternal = (action: NonNullable<FieldMeta['externalAction']>) => {
    switch (action) {
      case 'workHistory':
        dispatch(
          popupActions.updatePopups({
            workHistory: {
              open: true,
              workHistoryDisable: false,
              action: 'Add',
              item: {
                company_name: '',
                currentlyWorking: false,
                end_date: '',
                industry: '',
                roleType: [],
                start_date: '',
                title: '',
                _id: ''
              }
            }
          })
        );
        break;
      case 'educationHistory':
        dispatch(
          popupActions.updatePopups({
            education: {
              open: true,
              educationPopUpDisable: false,
              action: 'Add',
              item: {
                department: '',
                graduation_date: '',
                major: '',
                minor: [],
                university: '',
                _id: ''
              }
            }
          })
        );
        break;
      case 'socials':
        dispatch(popupActions.updatePopups({ social: true }));
        break;
      case 'learningObjectives':
        setOpenLearningObjectives(true);
        break;
      case 'memberAttributes':
        setAttributesEditorOpen(true);
        break;
      case 'subjects':
        // No dedicated popup — send the user to their profile page to manage
        navigate('/app/profile');
        break;
      default:
        break;
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleAttributesSaved = (memberAttributes: string[], declinedListIds: string[]) => {
    attributesPrompt.onSaved(memberAttributes, declinedListIds);
    setAttributesDone(true);
    setAttributesEditorOpen(false);
  };

  // Refresh user profile ONLY when an auxiliary popup transitions from open → closed,
  // so we re-evaluate isFilled for the complex fields after the user edits them.
  const profilePopupOpen = useAppSelector((state) => state.onboardingPopups.data.profile.open);
  const workHistoryOpen = useAppSelector(
    (state) => state.onboardingPopups.data.workHistory.open
  );
  const educationOpen = useAppSelector((state) => state.onboardingPopups.data.education.open);
  const socialOpen = useAppSelector((state) => state.onboardingPopups.data.social);

  const prevAnyOpen = useRef(false);
  useEffect(() => {
    const anyOpen = profilePopupOpen || workHistoryOpen || educationOpen || socialOpen;
    if (prevAnyOpen.current && !anyOpen) {
      dispatch(fetchUserProfile());
    }
    prevAnyOpen.current = anyOpen;
  }, [profilePopupOpen, workHistoryOpen, educationOpen, socialOpen, dispatch]);

  const checklistActive = hasMissing && !dismissed;
  // Attributes alone, nothing else missing: confirm them directly, no checklist.
  // Once saved in this mount the editor stays closed even if the derived
  // requirement lingers (a group left empty), so it can never reopen itself.
  const attributesAlone = !checklistActive && attributesRequired && !attributesDone;

  if (!checklistActive && !attributesAlone) return null;

  return (
    // Everything the prompt opens (the checklist, the attributes editor and
    // the auxiliary editors) renders on the onboarding card.
    <ThemeProvider theme={onboardingTheme}>
      {checklistActive && (
        <MissingMandatoryFieldsModal
          open
          fields={checklistFields}
          onSaved={handleSaved}
          onOpenExternal={handleOpenExternal}
          onSkip={handleSkip}
          allowSkip={isDosenTeam}
        />
      )}
      {(attributesEditorOpen || attributesAlone) && (
        <MemberAttributesModal
          open
          variant="welcomeBack"
          onClose={() => setAttributesEditorOpen(false)}
          onBack={checklistActive ? () => setAttributesEditorOpen(false) : undefined}
          orgMemberAttributes={attributesPrompt.orgMemberAttributes}
          selectedIds={attributesPrompt.selectedIds}
          declinedListIds={attributesPrompt.declinedListIds}
          teamRoles={attributesPrompt.teamRoles}
          onSave={handleAttributesSaved}
        />
      )}
      {/* Auxiliary editors — mounted here so they work regardless of current route */}
      <EditProfile />
      <WorkHistoryModal />
      <EducationModal />
      <SocialHandlesModal />
      {openLearningObjectives && (
        <LearningObjectivesNewModal
          open={openLearningObjectives}
          onClose={() => {
            setOpenLearningObjectives(false);
            dispatch(fetchUserProfile());
          }}
        />
      )}
    </ThemeProvider>
  );
}

